import React from 'react'
import * as rn from 'react-native'

import Feather from 'react-native-vector-icons/Feather'

import * as c from '../../common'
import * as r from '../../react-utils'

import * as cp from '../components'
import * as gs from '../gStyles'

import Text from './Text'

export type NavMenuProps = c.r<{
  availNonCustomEfficiencies: c.CustomerFields
  canHide?: boolean
  customerID: string
  hide?: boolean
  onPressCustomerReport(): void
  onPressCCTVSim(): void
  onPressCommissionCalc(): void
  onPressEfficiency(field: c.CustomerField): void
  onPressMediaCategory(category: string): void
  onPressPayMethod(): void
  onPressRoofClaims(): void
  onPressSolarSurvey(): void
}>

export default React.memo<NavMenuProps>(function NavMenu({
  availNonCustomEfficiencies,
  canHide,
  customerID,
  hide,
  onPressCustomerReport,
  onPressCCTVSim,
  onPressCommissionCalc,
  onPressEfficiency,
  onPressMediaCategory,
  onPressPayMethod,
  // onPressRoofClaims,
  onPressSolarSurvey,
}) {
  const t = r.useTheme()
  const gStyles = gs.useGlobalStyles('backdrop')
  const styles = gs.useThemedStyleSheet(themedStyles)

  //#region global
  const {
    availProducts,
    currProducts,
    toggleHIL,
    toggleRC,
    toggleSecurity,
    toggleSolar,
  } = r.useProducts(customerID)
  // Same list the customer page renders its media sections from, so the
  // Multimedia shortcuts always match the sections actually on the page.
  // Jaron (Monday 13241352402, 2026-10-08) tapped "Assessment" on a Smart
  // Home-only customer: that section is hidden for them, so the shortcut did
  // nothing, and "Smart Home" jumped to the CCTV Simulator photos instead of
  // the Smart Home section.
  const mediaCategories = r.useCategoryToMediaKinds(customerID)
  const productsSecurityAvail = React.useMemo(
    () => !!availProducts.find((o) => o.value === 'Security'),
    [availProducts],
  )
  const productsHILAvail = React.useMemo(
    () => !!availProducts.find((o) => o.value === 'HIL'),
    [availProducts],
  )
  const productsSolarAvail = React.useMemo(
    () => !!availProducts.find((o) => o.value === 'Solar'),
    [availProducts],
  )
  const productsRCAvail = React.useMemo(
    () => !!availProducts.find((o) => o.value === 'RC'),
    [availProducts],
  )
  //#endregion global
  //#region local
  const handleCustomPress = React.useCallback((): void => {
    onPressEfficiency('custom_fields')
  }, [onPressEfficiency])
  //#endregion local

  const checkActive = (
    <Feather color={t.backdrop.highlight} name="check-square" size={16} />
  )
  const checkInactive = (
    <Feather color={t.backdrop.color as string} name="square" size={16} />
  )

  return (
    <rn.View
      id="nav-menu"
      style={(() => {
        if (canHide) {
          return hide ? styles.containerHidden : styles.container
        }
        return styles.container
      })()}
    >
      <cp.Text size={10}>Shortcuts</cp.Text>
      <rn.TouchableOpacity onPress={onPressCustomerReport} style={styles.item}>
        <Text style={styles.itemText}>Customer Report</Text>
      </rn.TouchableOpacity>

      {(currProducts.includes('Security') ||
        (currProducts.length === 0 && c.CODENAME !== 'nirvana')) && (
        <rn.TouchableOpacity onPress={onPressCCTVSim} style={styles.item}>
          <Text style={styles.itemText}>CCTV Simulator</Text>
        </rn.TouchableOpacity>
      )}

      {(currProducts.includes('HIL') ||
        currProducts.includes('Security') ||
        currProducts.length === 0) && (
        <rn.TouchableOpacity onPress={onPressPayMethod} style={styles.item}>
          <Text style={styles.itemText}>Payment Method</Text>
        </rn.TouchableOpacity>
      )}

      {(currProducts.includes('Solar') || currProducts.length === 0) && (
        <rn.TouchableOpacity
          onPress={onPressCommissionCalc}
          style={styles.item}
        >
          <Text style={styles.itemText}>Solar Calculator</Text>
        </rn.TouchableOpacity>
      )}

      <rn.View style={gStyles.separatorH} />
      {gs.rowGap12}
      <cp.Text size={10}>Multimedia</cp.Text>
      {mediaCategories.map(([category]) => (
        <MediaCategoryItem
          category={category}
          key={category}
          onPress={onPressMediaCategory}
        />
      ))}
      {(currProducts.includes('Solar') || currProducts.length === 0) && (
        <rn.TouchableOpacity onPress={onPressSolarSurvey} style={styles.item}>
          <Text style={styles.itemText}>Site Survey</Text>
        </rn.TouchableOpacity>
      )}
      {/* {(currProducts.includes('RC') || currProducts.length === 0) && (
        <rn.TouchableOpacity onPress={onPressRoofClaims} style={styles.item}>
          <Text style={styles.itemText}>Roof Claims</Text>
        </rn.TouchableOpacity>
      )} */}

      <rn.View style={gStyles.separatorH} />
      {gs.rowGap12}
      <cp.Text size={10}>Products</cp.Text>

      {productsSecurityAvail && c.CODENAME !== 'nirvana' && (
        <rn.TouchableOpacity onPress={toggleSecurity} style={styles.item}>
          {currProducts.includes('Security') ? checkActive : checkInactive}
          {gs.colGap8}
          <Text style={styles.itemText}>Smart Home</Text>
        </rn.TouchableOpacity>
      )}

      {productsHILAvail && (
        <rn.TouchableOpacity onPress={toggleHIL} style={styles.item}>
          {currProducts.includes('HIL') ? checkActive : checkInactive}
          {gs.colGap8}
          <Text style={styles.itemText}>Home Improvements</Text>
        </rn.TouchableOpacity>
      )}

      {productsSolarAvail && (
        <rn.TouchableOpacity onPress={toggleSolar} style={styles.item}>
          {currProducts.includes('Solar') ? checkActive : checkInactive}
          {gs.colGap8}
          <Text style={styles.itemText}>Solar</Text>
        </rn.TouchableOpacity>
      )}

      {productsRCAvail && (
        <rn.TouchableOpacity onPress={toggleRC} style={styles.item}>
          {currProducts.includes('RC') ? checkActive : checkInactive}
          {gs.colGap8}
          <Text style={styles.itemText}>Roof Claims</Text>
        </rn.TouchableOpacity>
      )}

      <rn.View style={gStyles.separatorH} />
      {gs.rowGap12}
      <cp.Text size={10}>Adders</cp.Text>
      {/* Page order: the custom adders render after every other adder, so
          their shortcut goes last too (Monday 13241352402). */}
      {availNonCustomEfficiencies.map((field) => (
        <NavMenuItem
          field={field}
          key={field}
          onPress={onPressEfficiency}
          renderSeparator
        />
      ))}
      <rn.TouchableOpacity onPress={handleCustomPress} style={styles.item}>
        <Text style={styles.itemText}>Custom Adders</Text>
      </rn.TouchableOpacity>
    </rn.View>
  )
})

type NavMenuItemProps = c.r<{
  field: c.CustomerField
  onPress(field: c.CustomerField): void
  renderSeparator: boolean
}>

const NavMenuItem = React.memo<NavMenuItemProps>(function NavMenuItem({
  field,
  onPress,
  renderSeparator,
}) {
  const gStyles = gs.useGlobalStyles('backdrop')
  const styles = gs.useThemedStyleSheet(themedStyles)
  const handlePress = React.useCallback(() => {
    onPress(field)
  }, [field, onPress])

  return (
    <React.Fragment>
      <rn.TouchableOpacity onPress={handlePress} style={styles.item}>
        <Text style={styles.itemText}>{c.getFieldLabel(field)}</Text>
      </rn.TouchableOpacity>

      {renderSeparator && <rn.View style={gStyles.separatorH} />}
    </React.Fragment>
  )
})

type MediaCategoryItemProps = c.r<{
  category: string
  onPress(category: string): void
}>

const MediaCategoryItem = React.memo<MediaCategoryItemProps>(
  function MediaCategoryItem({ category, onPress }) {
    const styles = gs.useThemedStyleSheet(themedStyles)
    const handlePress = React.useCallback(() => {
      onPress(category)
    }, [category, onPress])

    return (
      <rn.TouchableOpacity onPress={handlePress} style={styles.item}>
        <Text style={styles.itemText}>{category}</Text>
      </rn.TouchableOpacity>
    )
  },
)

const touchSurfaceWidth = 24
const margin = 12

const themedStyles = gs.ThemedStyleSheet.create((t) => {
  const container = {
    backgroundColor: t.backdrop.backgroundColor,
    borderRadius: t.backdrop.borderRadius,
    bottom: margin,
    // @ts-ignore 'fixed' not available on mobile
    position: (r.isWeb ? 'fixed' : 'absolute') as 'absolute',
    right: Number(t.canvas.gap) * 2,
    /* Breadcrumb height + margin */
    top: 28 + margin,
    // @ts-ignore
    transition: 'right ease-in-out 0.2s',
    maxHeight: '100%', //`${r.isSafari() ? 96 : 100 / 0.78}vh`,
    overflow: 'scroll',
    padding: t.backdrop.gap || 0,
    ...gs.shadowFullSoft,
  } as const

  return {
    container,
    containerHidden: { ...container, right: -232, overflow: 'hidden' },
    dot: {
      backgroundColor: t.backdrop.highlight,
      borderRadius: Number.MAX_SAFE_INTEGER,
      height: 12,
      width: 12,
    },
    item: {
      ...gs.rowCentered,
      padding: t.backdrop.gap,
    },
    itemText: {
      color: t.backdrop.color,
      fontFamily: t.backdrop.fontFamily,
      fontSize: 14,
    },
    separator: {
      backgroundColor: t.backdrop.separator,
      height: 1,
      width: '80%',
    },
    touchSurface: {
      ...gs.deadCenter,
      position: 'absolute',
      right: 0,
      height: '100%',
      width: touchSurfaceWidth,
    },
  }
})
